import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { MultiShipmentChildShipmentChip } from "components/MultiShipmentChildShipmentChip";
import { NewTabLink } from "components/NewTabLink";
import { PaymentMethodRequiredWrapper } from "components/PaymentMethodRequiredWrapper";
import { RefreshPageButton } from "components/RefreshPageButton";
import { PublicShipmentShareButton } from "components/Shipment/PublicShipmentShareButton";
import { PublicShipmentShareModal } from "components/Shipment/PublicShipmentShareModal";
import { RouteTab } from "components/ShipmentOverviewTab/components/RouteTab";
import { ShipmentQATab } from "components/ShipmentOverviewTab/components/ShipmentQATab";
import { ExportShipmentToPDFModal } from "components/Shipper/components/ExportShipmentToPDFModal";
import { WhatAreMultiShipmentProjectsAlert } from "components/WhatAreMultiShipmentProjectsAlert";
import {
  ClientAppEvent,
  TOTAL_FREIGHT_COSTS_CARRIER_BLOG_URL,
} from "constants/index";
import {
  useCarrierShipmentPageContext,
  useDisclosure,
  useRoleContext,
} from "hooks";
import { carrierHasBookingWriteAccess, carrierHasLostShipment } from "utils";

import { QuotesOnlyShipmentBadge } from "../../../QuotesOnlyShipmentBadge";
import { ShipmentDetails } from "../../../Shipment/components/ShipmentDetails";
import { ShipmentStatusBadge } from "../../../ShipmentStatusBadge";
import { CarrierQuoteTab } from "../CarrierQuoteTab";
import { PartnerShipmentChip } from "../PartnerShipmentChip";
import { QuoteCountChip } from "../QuoteCountChip";
import { SharedBookingChip } from "../SharedBookingChip";
import { ShipmentOverviewSubheader } from "../ShipmentOverviewSubheader";
import { SubmitOrCancelQuote } from "../SubmitOrCancelQuote";
import { SubmitQuote } from "../SubmitQuote";
import { AddPaymentInformationLabel } from "./components/AddPaymentInformationLabel";
import { CarrierBookNow } from "./components/CarrierBookNow";
import { CarrierProjectBookNow } from "./components/CarrierProjectBookNow";
import { CarrierShipmentOverviewChat } from "./components/CarrierShipmentOverviewChat";
import { CarrierSideBarMenu } from "./components/CarrierSideBarMenu";
import { ChildShipmentProjectBookingsSection } from "./components/ChildShipmentProjectBookingsSection";
import { MultiShipmentProjectBookingsSection } from "./components/MultiShipmentProjectBookingsSection";
import { ShareBookingWithTeammatesModal } from "./components/ShareBookingWithTeammatesModal";
import { ShipmentCancellationSection } from "./components/ShipmentCancellationSection";
import { ShipmentDataSection } from "./components/ShipmentDataSection";
import { ShipmentTrackingLinkSection } from "./components/ShipmentTrackingLinkSection";
import { TotalFreightCostSection } from "./components/TotalFreightCostSection";
import { UpdateShipmentStateSection } from "./components/UpdateShipmentStateSection";
import { ShipmentDetailTitle } from "./ShipmentDetailTitle";

const SUBMIT_QUOTE_FORM_ID = "submit-quote-form";

export function ShipmentOverviewExpanded() {
  const { shipment, onRefresh, shipmentFetchError } =
    useCarrierShipmentPageContext();
  const {
    isOpen: pdfPreviewOpen,
    onOpen: onOpenPdfPreview,
    onClose: onClosePdfPreview,
  } = useDisclosure();
  const {
    isOpen: drawerOpen,
    onClose: handleDrawerClose,
    onOpen: handleDrawerOpen,
  } = useDisclosure(false);
  const {
    isOpen: shareBookingWithTeammatesOpen,
    onOpen: onShareBookingWithTeammatesOpen,
    onClose: onShareBookingWithTeammatesClose,
  } = useDisclosure(false);
  const {
    isOpen: shareModalOpen,
    onOpen: onOpenShareModal,
    onClose: onCloseShareModal,
  } = useDisclosure(false);
  const posthog = usePostHog();
  const { role } = useRoleContext();

  const handleOpenShareModal = () => {
    if (shipment) {
      posthog?.capture(ClientAppEvent.PUBLIC_SHIPMENT_SHARE_MODAL_OPENED, {
        shipment_id: shipment.info.id,
        source: "carrier",
        triggered_by_role: role?.id,
      });
    }
    onOpenShareModal();
  };

  const sectionHeaderType = "h5";

  const SECTION_SPACE = "4rem";

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                flex={1}
              >
                <ShipmentDetailTitle shipment={shipment} />
                <ShipmentStatusBadge
                  status={shipment?.shipment_status}
                  size="small"
                  lostByCarrier={carrierHasLostShipment(shipment)}
                />
                <SharedBookingChip
                  carrieAccessType={shipment?.carrier_access_type}
                  size="small"
                />
                <PartnerShipmentChip
                  partner={shipment?.is_partner_shipment ?? false}
                />
                <QuotesOnlyShipmentBadge
                  isQuotesOnly={shipment?.info?.type === "QUOTES_ONLY"}
                />
                <MultiShipmentChildShipmentChip
                  isMultiShipmentChildShipment={
                    shipment?.info.type === "MULTI_SHIPMENT_PROJECT_CHILD"
                  }
                />
                {shipment && <QuoteCountChip shipment={shipment} />}
                <RefreshPageButton
                  id="carrier-shipment-overview-refresh"
                  iconButton
                  hardReload={false}
                  onRefresh={onRefresh ?? (() => {})}
                />
                {shipment &&
                  carrierHasBookingWriteAccess(shipment) &&
                  shipment.public_token && (
                    <PublicShipmentShareButton onClick={handleOpenShareModal} />
                  )}
              </Stack>
              {shipment && carrierHasBookingWriteAccess(shipment) && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerOpen}
                  sx={{
                    ...(drawerOpen && { display: "none" }),
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          }
          subheader={
            shipment && <ShipmentOverviewSubheader shipment={shipment} />
          }
          action={<SubmitOrCancelQuote shipment={shipment} />}
        />
        <CardContent>
          <Box position="relative">
            <ErrorTypography error={shipmentFetchError} />

            <ShipmentCancellationSection
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Cancellation Reason
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          The reason provided by the shipper when cancelling the
                          shipment.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>

                  <EvotruxDivider />
                </>
              }
            />
            <ShipmentDataSection
              shipment={shipment}
              dividerComponent={
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mt="2rem"
                  >
                    <Typography variant={sectionHeaderType}>
                      Quote Competitiveness
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          See how your quote compares to the booked rate.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
            />
            <TotalFreightCostSection
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Total Freight Costs
                    </Typography>
                    <NewTabLink href={TOTAL_FREIGHT_COSTS_CARRIER_BLOG_URL}>
                      Learn more
                    </NewTabLink>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
            />
            <ShipmentTrackingLinkSection
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Shipment Tracking
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          Manage your booked shipment&apos;s tracking here.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
            />
            <UpdateShipmentStateSection
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Shipment Status
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          Manage your booked shipment&apos;s status here.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
            />
            <MultiShipmentProjectBookingsSection
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Project Bookings
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          Shipments booked by you in this project will collect
                          here.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
              shipmentId={shipment?.project?.project}
            />
            <ChildShipmentProjectBookingsSection
              dividerComponent={
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mt="1rem"
                  >
                    <Typography variant={sectionHeaderType}>
                      Related Project Bookings
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          The rest of bookings assigned to you in this project.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
              shipmentId={shipment?.project?.project}
            />
            {shipment &&
              (shipment?.shipment_status === "BOOKED" ||
                shipment?.shipment_status === "BOOKING_CANCELLED" ||
                shipment?.shipment_status === "DELIVERED" ||
                shipment?.shipment_status === "PICKED_UP") &&
              shipment?.info.type !== "MULTI_SHIPMENT_PROJECT" && (
                <CarrierShipmentOverviewChat
                  key={shipment.info.id}
                  dividerComponent={
                    <>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant={sectionHeaderType}>
                          Live Chat
                        </Typography>
                        <Tooltip
                          title={
                            <Typography variant="h5">
                              Speak to the shipper in real time here.
                            </Typography>
                          }
                        >
                          <InfoOutlinedIcon color="primary" fontSize="large" />
                        </Tooltip>
                      </Stack>
                      <EvotruxDivider />
                    </>
                  }
                />
              )}
            <CarrierBookNow
              shipment={shipment}
              sectionHeaderType={sectionHeaderType}
              sectionSpace={SECTION_SPACE}
            />
            <CarrierProjectBookNow
              shipment={shipment}
              sectionHeaderType={sectionHeaderType}
              sectionSpace={SECTION_SPACE}
            />

            {shipment?.info.booked_at == null && (
              <>
                <Stack
                  id={SUBMIT_QUOTE_FORM_ID}
                  direction="row"
                  mt={SECTION_SPACE}
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant={sectionHeaderType}>
                    Submit A Quote
                  </Typography>
                  <Tooltip
                    title={
                      <Typography variant="h5">
                        Your quote will be sent directly to the shipper for
                        review. You will be notified immediately if they book
                        your truck or send you a counteroffer.
                      </Typography>
                    }
                  >
                    <InfoOutlinedIcon color="primary" fontSize="medium" />
                  </Tooltip>
                </Stack>
                <EvotruxDivider />
                <SubmitQuote />
              </>
            )}

            <Stack
              direction="row"
              mt={SECTION_SPACE}
              alignItems="center"
              spacing={1}
            >
              <Typography variant={sectionHeaderType}>My Quotes</Typography>
              <Tooltip
                title={
                  <Typography variant="h5">
                    These are quotes you have already placed on this shipment.
                  </Typography>
                }
              >
                <InfoOutlinedIcon color="primary" fontSize="medium" />
              </Tooltip>
            </Stack>
            <EvotruxDivider />
            <CarrierQuoteTab shipment={shipment} />
            <Typography mt="2rem" variant={sectionHeaderType}>
              Shipment Details
            </Typography>
            <EvotruxDivider />
            <WhatAreMultiShipmentProjectsAlert shipment={shipment} />
            <ShipmentDetails shipment={shipment} />

            <Typography mt={SECTION_SPACE} variant={sectionHeaderType}>
              Route
            </Typography>
            <EvotruxDivider />
            <RouteTab shipment={shipment} />

            <Stack
              direction="row"
              mt={SECTION_SPACE}
              alignItems="center"
              spacing={1}
            >
              <Typography variant={sectionHeaderType}>
                Questions & Answers
              </Typography>
              <Tooltip
                title={
                  <Typography variant="h5">
                    Ask the shipper a question about their load. All questions
                    and answers are publicly visible. Do not discuss rates or
                    send contact information here.
                  </Typography>
                }
              >
                <InfoOutlinedIcon color="primary" fontSize="large" />
              </Tooltip>
            </Stack>
            <EvotruxDivider />
            {!shipment?.is_partner_shipment ? (
              <PaymentMethodRequiredWrapper
                enableWithSubscriptionPlan="CARRIER_PAY_PER_BOOKING"
                noPaymentMethodComponent={<AddPaymentInformationLabel />}
              >
                <ShipmentQATab shipment={shipment} />
              </PaymentMethodRequiredWrapper>
            ) : (
              <ShipmentQATab shipment={shipment} />
            )}
          </Box>
        </CardContent>
      </Card>
      <CarrierSideBarMenu
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        onShareBookingWithTeammatesOpen={onShareBookingWithTeammatesOpen}
        onOpenPdfPreview={onOpenPdfPreview}
      />
      {shipment && (
        <ExportShipmentToPDFModal
          open={pdfPreviewOpen}
          onClose={onClosePdfPreview}
          shipment={shipment}
        />
      )}
      {shipment && (
        <ShareBookingWithTeammatesModal
          shipment={shipment}
          onModalClose={onShareBookingWithTeammatesClose}
          onTeammatesAdded={onRefresh ?? (() => {})}
          modalOpen={shareBookingWithTeammatesOpen}
        />
      )}
      {shipment &&
        carrierHasBookingWriteAccess(shipment) &&
        shipment.public_token && (
          <PublicShipmentShareModal
            open={shareModalOpen}
            onClose={onCloseShareModal}
            publicToken={shipment.public_token}
            shipmentId={shipment.info.id}
            source="carrier"
          />
        )}
    </>
  );
}
